// React
import React, { useState, createContext, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'

// Hooks
import { useSettings } from 'shared/utils/useSettings'

export const PickerContext = createContext({
  mode: null,
  selection: 'from',
  calendar: 'from',
  type: null,
  openingHours: null,
  allowPast: false,
  hasInitialValues: false,
  setSelection: () => {},
  onChange: () => {},
  onValidate: () => {},
  isDaySelectable: () => {},
  isTimeSelectable: () => {},
  getSelectableTimeslots: () => {},
  setDefaultTime: () => {},
  refs: {
    fromLocationRef: {
      current: null
    },
    toLocationRef: {
      current: null
    },
    fromDateRef: {
      current: null
    },
    fromTimeRef: {
      current: null
    },
    tillDateRef: {
      current: null
    },
    tillTimeRef: {
      current: null
    },
    timeslotsRef: {
      current: null
    },
    durationsRef: {
      current: null
    }
  }
})

export const PickerContextProvider = ({
  mode: propsMode,
  type = 'order',
  openingHours,
  useTimes: propsUseTimes,
  allowPast,
  hasInitialValues,
  isDaySelectable,
  isTimeSelectable,
  getSelectableTimeslots,
  setDefaultTime,
  onValidate,
  children
}) => {
  // Shared refs
  const fromLocationRef = useRef(null)
  const toLocationRef = useRef(null)
  const fromDateRef = useRef(null)
  const fromTimeRef = useRef(null)
  const tillDateRef = useRef(null)
  const tillTimeRef = useRef(null)

  // Mode specific refs
  const timeslotsRef = useRef(null)
  const durationsRef = useRef(null)

  // State
  // Possible values: 'from', 'from_time', 'to', 'to_time', null
  const [selection, setSelection] = useState('from')
  // Possible values: 'from', 'to'
  const [calendar, setCalendar] = useState('from')

  // Store
  const pickerUseTimes = useSettings('store.use_times')
  const pickerMode = useSettings('store.period_type')
  const usePickerRestrictions = useSettings('store.enable_period_restrictions_to_manual_orders')

  const mode = propsMode || pickerMode

  const shouldUseTimes = useCallback(() => {
    // Always disable time selection for timeslots
    if (mode === 'timeslot_fixed') return false

    // Use the prop value if it's defined
    if (propsUseTimes !== undefined) return propsUseTimes

    // Use the "Enable time selection" setting
    // if rental period restrictions are applied to manual orders or if the picker is used in the cart
    if (usePickerRestrictions || type === 'cart') return pickerUseTimes

    // Otherwise, enable time selection
    return true
  }, [type, mode, propsUseTimes, usePickerRestrictions, pickerUseTimes])

  const useTimes = shouldUseTimes()

  const handleSetCalendar = useCallback((calendar) => {
    switch (calendar) {
      case 'from':
      case 'to':
        setCalendar(calendar)
        break
      default:
        throw new Error(`
          Invalid calendar value "${calendar}" passed to "setCalendar".
          Allowed values are: "from", "to".
        `)
    }
  }, [])

  const handleSetSelection = useCallback((selection) => {
    // Check if the selection value is valid
    switch (selection) {
      case 'from':
      case 'from_time':
      case 'to':
      case 'to_time':
      case 'timeslot':
      case 'duration':
      case null:
        setSelection(selection)
        break
      default:
        throw new Error(`
          Invalid selection value "${selection}" passed to "setSelection".
          Allowed values are: "from", "from_time", "to", "to_time", "timeslot", "duration", null.
        `)
    }

    // Change the calendar accordingly to the selection
    switch (selection) {
      case 'from':
        handleSetCalendar('from')
        break
      case 'to':
        handleSetCalendar('to')
        break
      default:
        break
    }
  }, [])

  const handleFreeSelectionChange = useCallback(
    (selection) => {
      switch (selection) {
        case 'from':
          handleSetCalendar('to')

          if (useTimes) {
            handleSetSelection('from_time')

            return setTimeout(() => {
              fromTimeRef.current?._tippy?.show()
            }, 1)
          }

          handleSetSelection('to')

          setTimeout(() => {
            tillDateRef.current?._tippy?.show()
          }, 1)
          break
        case 'from_time':
          handleSetSelection('to')

          setTimeout(() => {
            tillDateRef.current?._tippy?.show()
          }, 1)
          break
        case 'to':
          handleSetCalendar('from')

          if (useTimes) {
            handleSetSelection('to_time')

            return setTimeout(() => {
              tillTimeRef.current?._tippy?.show()
            }, 1)
          }
          break
        case 'to_time':
          handleSetCalendar('from')
          handleSetSelection(null)
          break
        default:
          break
      }
    },
    [useTimes, type]
  )

  const handleDurationSelectionChange = useCallback(
    (selection) => {
      switch (selection) {
        case 'from':
          if (useTimes) {
            handleSetSelection('from_time')

            return setTimeout(() => {
              fromTimeRef.current?._tippy?.show()
            }, 1)
          }

          handleSetSelection(null)
          break
        case 'from_time':
          handleSetSelection('to_time')

          setTimeout(() => {
            tillTimeRef.current?._tippy?.show()
          }, 1)
          break
        case 'to_time':
          handleSetSelection(null)
          break
        case 'duration':
          durationsRef.current?._tippy?.hide()

          handleSetSelection('from')

          setTimeout(() => {
            fromDateRef.current?._tippy?.show()
          }, 1)
          break
        default:
          break
      }
    },
    [useTimes, type]
  )

  const handleTimeslotSelectionChange = useCallback(
    (selection) => {
      switch (selection) {
        case 'from':
          handleSetSelection('timeslot')

          return setTimeout(() => {
            timeslotsRef.current?._tippy?.show()
          }, 1)
        case 'timeslot':
          timeslotsRef.current?._tippy?.hide()

          handleSetSelection(null)
          break
        default:
          break
      }
    },
    [useTimes, type]
  )

  const handlePickerChange = useCallback(
    (selection) => {
      switch (mode) {
        case 'freely':
          return handleFreeSelectionChange(selection)
        case 'timeslot_duration':
          return handleDurationSelectionChange(selection)
        case 'timeslot_fixed':
          return handleTimeslotSelectionChange(selection)
        default:
          throw new Error(`
            Invalid mode value "${mode}" passed used in PickerProvider.
            Allowed values are: "freely", "timeslot_duration", "timeslot_fixed".
          `)
      }
    },
    [mode, useTimes, handleFreeSelectionChange, handleDurationSelectionChange, handleTimeslotSelectionChange]
  )

  return (
    <PickerContext.Provider
      value={{
        mode,
        allowPast,
        useTimes,
        selection,
        calendar,
        type,
        openingHours,
        hasInitialValues,
        onChange: handlePickerChange,
        onValidate,
        setSelection: handleSetSelection,
        isDaySelectable,
        isTimeSelectable,
        getSelectableTimeslots,
        setDefaultTime,
        refs: {
          fromLocationRef,
          toLocationRef,
          fromDateRef,
          fromTimeRef,
          tillDateRef,
          tillTimeRef,
          timeslotsRef,
          durationsRef
        }
      }}
    >
      {children}
    </PickerContext.Provider>
  )
}

PickerContextProvider.propTypes = {
  children: PropTypes.node.isRequired,
  mode: PropTypes.oneOf(['freely', 'timeslot_duration', 'timeslot_fixed', 'undefined']),
  type: PropTypes.oneOf(['order', 'cart']).isRequired,
  openingHours: PropTypes.object,
  allowPast: PropTypes.bool,
  useTimes: PropTypes.bool,
  hasInitialValues: PropTypes.bool.isRequired,
  isDaySelectable: PropTypes.func.isRequired,
  isTimeSelectable: PropTypes.func.isRequired,
  getSelectableTimeslots: PropTypes.func.isRequired,
  setDefaultTime: PropTypes.func.isRequired,
  onValidate: PropTypes.func
}
